<template>
  <div class="kb-row">
    <div>
      <h4 class="kb-row-title">
        Check-in
      </h4>
      <p
        v-if="loading"
        class="kb-row-hint"
      >
        <KbSpinner class="mr-1.5" />
        Loading check-in…
      </p>
      <p
        v-else-if="checkinInfo?.checkedIn"
        class="kb-row-meta m-0 mt-0.5"
      >
        {{ formatCheckinTime(checkinInfo.timestamp) }} · by {{ checkinInfo.checkedInByName }}<template v-if="checkinInfo.method">
          · {{ formatMethod(checkinInfo.method) }}
        </template><template v-if="checkinInfo.captainName">
          · captain {{ checkinInfo.captainName }}
        </template>
      </p>
      <p
        v-else
        class="kb-row-hint"
      >
        Not checked in for the upcoming parade yet
      </p>
    </div>

    <div class="kb-row-aside">
      <span
        v-if="!loading"
        class="kb-tag"
        :class="checkinInfo?.checkedIn ? 'kb-tag-ok' : 'kb-tag-neutral'"
      >{{ checkinInfo?.checkedIn ? 'Checked in' : 'Not checked in' }}</span>
      <template v-if="!loading">
        <Button
          v-if="checkinInfo?.checkedIn"
          label="Undo"
          icon="pi pi-undo"
          text
          severity="danger"
          size="small"
          :loading="undoLoading"
          @click="handleUndoCheckin"
        />
        <Button
          v-else
          label="Check in"
          icon="pi pi-check"
          outlined
          severity="secondary"
          size="small"
          :loading="checkInLoading"
          @click="handleCheckIn"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import Button from 'primevue/button'
import type { Member } from '~/shared/types/member'
import type { CheckinInfo } from '~/shared/types/checkin'
import { formatDateWithTime } from '~/utils/dateUtils'

interface Props {
  member: Member
}

const props = defineProps<Props>()
const emit = defineEmits(['updated'])

const { getCheckinStatus } = useCheckinStatus()
const { undoCheckin, checkinMember } = useCheckin()
const { paradeYear } = useParadeYear()
const toast = useToast()

const loading = ref(true)
const undoLoading = ref(false)
const checkInLoading = ref(false)
const checkinInfo = ref<CheckinInfo | null>(null)

async function loadCheckinStatus() {
  loading.value = true
  try {
    checkinInfo.value = await getCheckinStatus(props.member.id)
  }
  catch (error) {
    console.error('Error loading check-in status:', error)
  }
  finally {
    loading.value = false
  }
}

async function handleCheckIn() {
  checkInLoading.value = true
  try {
    // Calculate ticket counts
    const yearPurchases = props.member.purchases?.[paradeYear.value]
    const vipCount = yearPurchases?.vip?.filter(
      p => p.status === 'completed',
    ).length ?? 0
    const parkingCount = yearPurchases?.parking?.filter(
      p => p.status === 'completed',
    ).length ?? 0
    const childrenCount = yearPurchases?.children?.reduce(
      (sum, purchase) => sum + (purchase.status === 'completed' ? (purchase.quantity ?? 0) : 0),
      0,
    ) ?? 0

    const success = await checkinMember(
      props.member.id,
      'admin_manual',
      // captainInfo — only recorded for captain_bulk check-ins. The acting
      // user is already captured as `checkedInBy` inside the composable; this
      // slot previously received their uid, which it silently ignored.
      undefined,
      undefined,
      {
        name: props.member.name || props.member.email || 'Member',
        subkreweName: props.member.subkrewe?.name,
        vipCount,
        parkingCount,
        childrenCount,
      },
    )

    if (success) {
      await loadCheckinStatus()
      emit('updated')
    }
    else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to check in member',
        life: 3000,
      })
    }
  }
  catch (error) {
    console.error('Error checking in member:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'An error occurred',
      life: 3000,
    })
  }
  finally {
    checkInLoading.value = false
  }
}

async function handleUndoCheckin() {
  undoLoading.value = true
  try {
    const success = await undoCheckin(props.member.id)

    if (success) {
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Check-in has been undone',
        life: 3000,
      })
      checkinInfo.value = null
      emit('updated')
    }
    else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to undo check-in',
        life: 3000,
      })
    }
  }
  catch (error) {
    console.error('Error undoing check-in:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'An error occurred',
      life: 3000,
    })
  }
  finally {
    undoLoading.value = false
  }
}

function formatCheckinTime(timestamp?: Date): string {
  if (!timestamp) return 'Unknown'
  return formatDateWithTime(timestamp)
}

function formatMethod(method: string): string {
  const methods: Record<string, string> = {
    individual: 'Individual',
    captain_bulk: 'Captain Bulk Check-in',
    admin_manual: 'Admin Manual',
  }
  return methods[method] || method
}

onMounted(() => {
  loadCheckinStatus()
})
</script>
